import { client } from 'clients/eyekia';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Image, Asset, Primitive } from '@sophya/eyekia';
import { AssetDO } from './Asset';
import { BaseStore } from './BaseStore';
import { FabricStore2 as FabricStore } from './fabric2/FabricStore2';
import { OrientationDO, OrientationKey } from './Orientation';
import { PrimitiveDO } from './Primitive';
import { AssetType, FabricInterface, RootStore } from './RootStore';
import { StructureDO } from './Structure';
import { AutoId } from '../common/AutoId';

export class StructureStore extends BaseStore {
  floors = new Map<string, StructureDO>();
  walls = new Map<string, StructureDO>();

  items(type: AssetType) {
    switch (type) {
      case AssetType.Floor:
        return this.floors;
      case AssetType.Wall:
        return this.walls;
        break;
    }
  }

  constructor(private root: RootStore) {
    super();
    makeObservable(this, {
      floors: observable,
      walls: observable,
      loadingStructures: observable,
    });

    this.handleLoadStructures();
  }

  loadingStructures: boolean = false;

  handleLoadStructures = async () => {
    this.loadingStructures = true;
    const resFloor = await client().v1.draftFloorList();
    const resWall = await client().v1.draftWallList();
    runInAction(() => {
      for (const floor of resFloor.data.floors) {
        const { id, data } = floor;

        const floorDo = new StructureDO(id, this, AssetType.Floor);
        //@ts-ignore
        floorDo.updateFromSchema(data);
        this.floors.set(id, floorDo);
      }

      for (const wall of resWall.data.walls) {
        const { id, data } = wall;

        const wallDo = new StructureDO(id, this, AssetType.Wall);
        //@ts-ignore
        wallDo.updateFromSchema(data);
        this.walls.set(id, wallDo);
      }

      this.loadingStructures = false;
    });
  };

  handleLoadStructureById = async (structureId: string, type: AssetType): Promise<StructureDO | null> => {
    console.log('🏗️ Loading single structure:', structureId, 'type:', AssetType[type]);

    // First check if it's already loaded
    const existing = this.getStructure(type, structureId);
    if (existing) {
      console.log('🏗️ Structure already loaded:', structureId);
      return existing;
    }

    try {
      // Try to load the single structure first via the authenticated draft endpoint
      try {
        switch (type) {
          case AssetType.Floor: {
            const res = await client().v1.draftFloorDetail(structureId);
            console.log('🏗️ Draft floor fetch successful:', res.data);

            runInAction(() => {
              const structureDO = new StructureDO(structureId, this, type);
              //@ts-ignore
              structureDO.updateFromSchema(res.data);
              this.floors.set(structureId, structureDO);
            });

            return this.getStructure(type, structureId);
          }
          case AssetType.Wall: {
            const res = await client().v1.draftWallDetail(structureId);
            console.log('🏗️ Draft wall fetch successful:', res.data);

            runInAction(() => {
              const structureDO = new StructureDO(structureId, this, type);
              //@ts-ignore
              structureDO.updateFromSchema(res.data);
              this.walls.set(structureId, structureDO);
            });

            return this.getStructure(type, structureId);
          }
        }
      } catch (directError) {
        console.log('🏗️ Draft fetch failed, falling back to list loading:', directError);
      }

      // Fall back to loading all structures if single structure loading fails
      console.log('🏗️ Single structure fetch failed, loading all structures...');

      // Wait for existing loading to complete if it's already in progress
      if (this.loadingStructures) {
        console.log('🏗️ Waiting for existing structure loading to complete...');
        while (this.loadingStructures) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } else {
        // Only start loading if not already loading
        await this.handleLoadStructures();
      }

      // Return the structure after loading all
      return this.getStructure(type, structureId);

    } catch (error) {
      console.error('🏗️ Failed to load structure:', error);
      return null;
    }
  };

  currentStructure: string;

  async handleCreateStructure(type: AssetType) {
    switch (type) {
      case AssetType.Floor:
        return this.createFloor();
      case AssetType.Wall:
        return this.createWall();
    }
  }

  async handlePublishAll(type: AssetType) {
    const structures = Array.from(this.items(type), ([, structureDO]) => structureDO);
    const publishTasks = structures.map((structure) => {
      if (!structure.published.value || structure.modifiedSinceLastPublish) {
        return structure.publishAsset();
      }
    });
    await Promise.all(publishTasks);
  }

  createFloor = async (): Promise<void> => {
    const res = await client().v1.floorCreate({});
    const id = res.data.floorId;
    runInAction(() => {
      this.floors.set(id, new StructureDO(id, this, AssetType.Floor));
    });
  };
  createWall = async (): Promise<void> => {
    const res = await client().v1.wallCreate({});
    const id = res.data.wallId;
    runInAction(() => {
      this.walls.set(id, new StructureDO(id, this, AssetType.Wall));
    });
  };

  handleDispose(): void {
    for (const floor of this.floors.values()) {
      floor.dispose();
    }
    for (const wall of this.walls.values()) {
      wall.dispose();
    }
  }

  getStructure(type: AssetType, id: string) {
    switch (type) {
      case AssetType.Floor:
        return this.floors.get(id);

      case AssetType.Wall:
        return this.walls.get(id);
    }
  }

  handleDeleteStructure(type: AssetType, id: string) {
    switch (type) {
      case AssetType.Floor:
        this.deleteFloor(id);
        break;
      case AssetType.Wall:
        this.deleteWall(id);
        break;
    }
  }

  deleteFloor(id: string): void {
    runInAction(() => {
      this.floors.delete(id);
    });
    this.networkDeleteFloor(id);
  }
  networkDeleteFloor = async (id: string): Promise<void> => {
    await client().v1.floorDelete(id);
  };

  deleteWall(id: string): void {
    runInAction(() => {
      this.walls.delete(id);
    });
    this.networkDeleteWall(id);
  }
  networkDeleteWall = async (id: string): Promise<void> => {
    await client().v1.wallDelete(id);
  };
}
